import type { Writable } from "svelte/store";
import type { JottacloudConfig } from "./fs";
import { FS, loadSavedFsConfig, saveFsConfig } from "./fs";
import { FsDir } from "./fsentry";
import type { HttpProgress } from "./http";
import { mkURL } from "./http";
import { md5OfBlob } from "./md5";
import { throwIf } from "./util";

// The browser cannot call Jottacloud. File calls go through /api/jottacloud.
// Tokens are request headers, not query parameters.
//
// A login trades a personal login token for an access token that lives about
// an hour and a refresh token. Every refresh returns a new refresh token and
// ends the old one, so a new pair is saved at once: a lost pair means the user
// has to connect again.

export const prop_jotta_mime = "jotta_mime";
export const prop_jotta_md5 = "jotta_md5";

// refresh this long before the access token ends
const tokenMargin = 60_000;

class JottaError extends Error {
  status: number;
  constructor(msg: string, status: number) {
    super(msg);
    this.status = status;
  }
}

class FileSystemJottacloud extends FS {
  supportsDirRename = true;

  constructor(config: JottacloudConfig) {
    super(config);
  }

  get jottaConfig(): JottacloudConfig {
    return this.config as JottacloudConfig;
  }

  async readDir(dirPath: string, force: boolean, progress: Writable<any>): Promise<FsDir> {
    throwIf(dirPath == "", "empty dir");
    return this.loadDir(dirPath, force, progress, async () => {
      let js = await jottaCall(this.jottaConfig, jottaURL(this.jottaConfig, "list", "dir", dirPath));
      if (!Array.isArray(js)) {
        throw new Error("jottacloud list failed");
      }
      return FsDir.fromWire(dirPath, js);
    });
  }

  async getPublicURL(path: string): Promise<string> {
    let blob = await this.downloadFile(path, () => {});
    return URL.createObjectURL(blob);
  }

  async downloadFile(path: string, httpProgress: HttpProgress): Promise<Blob> {
    let url = jottaURL(this.jottaConfig, "download", "path", path);
    let xhr = await jottaSend(this.jottaConfig, "GET", url, {}, undefined, httpProgress, "blob");
    return xhr.response as Blob;
  }

  // Jottacloud wants the MD5 of the content before it accepts an upload. A
  // file with the same name gets a new revision.
  async uploadFile(path: string, file: Blob, httpProgress: HttpProgress): Promise<void> {
    let md5 = await md5OfBlob(file);
    let args = ["path", path];
    if (file instanceof File && file.lastModified > 0) {
      args.push("modified", String(file.lastModified));
    }
    let url = jottaURL(this.jottaConfig, "upload", ...args);
    await jottaSend(this.jottaConfig, "PUT", url, { "X-Jotta-Md5": md5 }, file, httpProgress);
  }

  async rename(path: string, newPath: string): Promise<void> {
    throwIf(path == newPath, "path must be != newPath");
    let url = jottaURL(this.jottaConfig, "rename", "path", path, "newPath", newPath);
    await jottaCall(this.jottaConfig, url, "POST");
  }

  async createDirectory(path: string): Promise<void> {
    await jottaCall(this.jottaConfig, jottaURL(this.jottaConfig, "mkdir", "path", path), "POST");
  }

  // Jottacloud moves the entry to its trash and empties the trash after 30
  // days. A folder goes with everything in it.
  async deleteFile(path: string): Promise<void> {
    await jottaCall(this.jottaConfig, jottaURL(this.jottaConfig, "delete", "path", path), "POST");
  }
}

function jottaURL(config: JottacloudConfig, op: string, ...args: string[]): string {
  return mkURL("/api/jottacloud/" + op, "user", config.user, ...args);
}

async function jottaFetch(url: string, method: string, headers: Record<string, string>): Promise<any> {
  let rsp: Response;
  try {
    rsp = await fetch(url, { method, headers });
  } catch {
    throw new Error("jottacloud request failed");
  }
  let text = await rsp.text();
  let js: any = {};
  if (text) {
    try {
      js = JSON.parse(text);
    } catch {
      js = {};
    }
  }
  if (!rsp.ok) {
    throw new JottaError(js.error || `jottacloud ${rsp.status}`, rsp.status);
  }
  return js;
}

function setTokens(c: JottacloudConfig, js: any) {
  throwIf(!js.accessToken, "jottacloud did not return an access token");
  c.accessToken = js.accessToken;
  if (js.refreshToken) {
    c.refreshToken = js.refreshToken;
  }
  let seconds = Number(js.expiresIn) > 0 ? Number(js.expiresIn) : 3600;
  c.tokenExpires = Date.now() + seconds * 1000;
}

/**
 * Trade a personal login token for access. Fills the tokens and the account
 * of the config. The login token works once.
 */
export async function jottaLogin(c: JottacloudConfig, loginToken: string): Promise<void> {
  let js = await jottaFetch("/api/jottacloud/login", "POST", { "X-Jotta-Login": loginToken });
  throwIf(!js.user || !js.refreshToken || !js.tokenURL, "jottacloud login failed");
  setTokens(c, js);
  c.user = js.user;
  c.email = js.email || "";
  c.tokenURL = js.tokenURL;
}

function tokenFresh(c: JottacloudConfig): boolean {
  return c.accessToken != "" && Date.now() < c.tokenExpires - tokenMargin;
}

// Runs fn while no other tab of this browser runs it for the same account.
async function withAccountLock(c: JottacloudConfig, fn: () => Promise<void>): Promise<void> {
  let locks = typeof navigator != "undefined" ? navigator.locks : undefined;
  if (!locks) {
    return fn();
  }
  await locks.request("filerion-jottacloud-" + c.user, fn);
}

async function refreshTokens(c: JottacloudConfig, rejected: string): Promise<void> {
  await withAccountLock(c, async () => {
    // Another tab may have refreshed already. Its tokens are the saved ones,
    // and the refresh token we hold is then dead.
    let saved = loadSavedFsConfig(c) as JottacloudConfig | null;
    if (saved && saved.refreshToken && saved.tokenExpires > c.tokenExpires) {
      c.accessToken = saved.accessToken;
      c.refreshToken = saved.refreshToken;
      c.tokenExpires = saved.tokenExpires;
      if (tokenFresh(c) && c.accessToken != rejected) {
        return;
      }
    }
    let url = mkURL("/api/jottacloud/refresh", "tokenURL", c.tokenURL);
    let js = await jottaFetch(url, "POST", { "X-Jotta-Refresh": c.refreshToken });
    setTokens(c, js);
    saveFsConfig(c);
  });
}

const refreshing = new Map<string, Promise<void>>();

/**
 * The access token of the account, refreshed when it is about to end.
 * rejected is an access token the server just refused: it is not returned
 * again, even when its time is not up.
 */
export async function jottaEnsureToken(c: JottacloudConfig, rejected = ""): Promise<string> {
  if (tokenFresh(c) && c.accessToken != rejected) {
    return c.accessToken;
  }
  let pending = refreshing.get(c.user);
  if (!pending) {
    pending = refreshTokens(c, rejected).finally(() => {
      refreshing.delete(c.user);
    });
    refreshing.set(c.user, pending);
  }
  await pending;
  return c.accessToken;
}

// Runs a request with the access token. Tries once more with a new token when
// the server says the token is no longer good.
async function withToken<T>(c: JottacloudConfig, fn: (token: string) => Promise<T>): Promise<T> {
  let token = await jottaEnsureToken(c);
  try {
    return await fn(token);
  } catch (e) {
    if (!(e instanceof JottaError) || e.status != 401) {
      throw e;
    }
    token = await jottaEnsureToken(c, token);
    return await fn(token);
  }
}

function jottaCall(c: JottacloudConfig, url: string, method = "GET"): Promise<any> {
  return withToken(c, (token) => jottaFetch(url, method, { "X-Jotta-Token": token }));
}

function jottaSend(
  c: JottacloudConfig,
  method: string,
  url: string,
  headers: Record<string, string>,
  data: Blob | undefined,
  onProgress: HttpProgress | undefined,
  responseType?: XMLHttpRequestResponseType,
): Promise<XMLHttpRequest> {
  return withToken(c, (token) => {
    let all = { ...headers, "X-Jotta-Token": token };
    return jottaXhr(method, url, all, data, onProgress, responseType);
  });
}

function jottaXhr(
  method: string,
  url: string,
  headers: Record<string, string>,
  data: Blob | undefined,
  onProgress: HttpProgress | undefined,
  responseType?: XMLHttpRequestResponseType,
): Promise<XMLHttpRequest> {
  return new Promise((resolve, reject) => {
    let xhr = new XMLHttpRequest();
    xhr.open(method, url);
    if (responseType) {
      xhr.responseType = responseType;
    }
    for (let [k, v] of Object.entries(headers)) {
      xhr.setRequestHeader(k, v);
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr);
        return;
      }
      jottaErrText(xhr).then(
        (msg) => reject(new JottaError(msg, xhr.status)),
        () => reject(new JottaError(`jottacloud ${xhr.status}`, xhr.status)),
      );
    };
    xhr.onerror = () => {
      reject(new Error("jottacloud request failed"));
    };
    if (onProgress) {
      let started = Date.now();
      let report = (ev: ProgressEvent) => {
        let total = ev.lengthComputable ? ev.total : 0;
        onProgress(ev.loaded, total, Date.now() - started);
      };
      if (method == "PUT") {
        xhr.upload.onprogress = report;
      } else {
        xhr.onprogress = report;
      }
    }
    if (data) {
      xhr.send(data);
    } else {
      xhr.send();
    }
  });
}

async function jottaErrText(xhr: XMLHttpRequest): Promise<string> {
  let text = "";
  if (xhr.responseType == "blob" && xhr.response instanceof Blob) {
    text = await xhr.response.text();
  } else if (xhr.responseType == "" || xhr.responseType == "text") {
    text = xhr.responseText || "";
  }
  if (text) {
    try {
      let js = JSON.parse(text);
      if (js.error) {
        return String(js.error);
      }
    } catch {
      // keep the status
    }
  }
  return `jottacloud ${xhr.status}`;
}

export function makeFileSystemJottacloud(config: JottacloudConfig): FileSystemJottacloud {
  return new FileSystemJottacloud(config);
}
